import { forkJoin } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormBuilder } from '@angular/forms';
import { SelectItem, MenuItem, ConfirmationService, SelectItemGroup } from 'primeng/api';
import { ReportModes, ToolboxCandidateModel } from 'projects/core/src/lib/models/toolbox-candidate.model';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { PartnerModel } from 'projects/core/src/lib/models/partner.model';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { Validator } from 'projects/core/src/lib/helpers/validator';
import { OnlinetestModel } from 'projects/core/src/lib/models/onlinetest.model';
import { LANGUAGES, SALUTATIONS } from 'projects/core/src/lib/constants';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

@Component({
  selector: 'app-toolbox-candidate-basedata',
  templateUrl: './toolbox-candidate-basedata.component.html',
  styleUrls: ['./toolbox-candidate-basedata.component.scss']
})
export class ToolboxCandidateBasedataComponent extends BaseForm implements OnInit {

  @Input() order: ToolboxCandidateModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public form: UntypedFormGroup = this.fb.group({
    businessunit: new UntypedFormControl(null, [Validators.required]),
    onlinetest: new UntypedFormControl(null, [Validators.required]),
    language: new UntypedFormControl(null, [Validators.required]),
    candidate: this.fb.group({
      gender: new UntypedFormControl(null),
      firstname: new UntypedFormControl(null, [Validators.required]),
      lastname: new UntypedFormControl(null, [Validators.required]),
      email: new UntypedFormControl(null, [Validators.required, CustomValidators.email()]),
      phone: new UntypedFormControl(null),
    }),
    hiringmanager: this.fb.group({
      gender: new UntypedFormControl(null),
      firstname: new UntypedFormControl(null, [Validators.required]),
      lastname: new UntypedFormControl(null, [Validators.required]),
      email: new UntypedFormControl(null, [Validators.required, CustomValidators.email()]),
      phone: new UntypedFormControl(null),
      mobile: new UntypedFormControl(null),
    }),
    report: this.fb.group({
      type: new UntypedFormControl(null, [Validators.required]),
      gender: new UntypedFormControl(null),
      firstname: new UntypedFormControl(null),
      lastname: new UntypedFormControl(null),
      email: new UntypedFormControl(null, [CustomValidators.email()])
    })
  });

  public items: Array<MenuItem> = [];
  public optionsBusinessunits: Array<SelectItemGroup> = [];
  public optionsPartners: Array<SelectItemGroup> = [];
  public optionsSalutations: Array<SelectItem> = [];
  public optionsLanguages: Array<SelectItem> = [];
  public optionsReporttypes: Array<SelectItem> = [];

  public ReportModes = ReportModes;
  public LANGUAGES = LANGUAGES;

  public get hasRecipient(): boolean {
    if (this.inEdit) {
      return this.form.get('report.type').value === ReportModes.Other || this.form.get('report.type').value === ReportModes.Both;
    } else {
      return this.order.report_recipient_type === ReportModes.Other || this.order.report_recipient_type === ReportModes.Both;
    }
  }

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private auth: AuthStateService,
    private confirmationService: ConfirmationService,
    public transloco: TranslocoService
) {
  super();
}

  ngOnInit(): void {
    this.loading = true;
    this.buildMenu();
    this.api.getToolboxFormData().subscribe({
      next: (result: any) => {
        if (Validator.isArray(result.data.businessunits)) {
          this.buildBusinessunits(result.data.businessunits);
        }
        if (Validator.isArray(result.data.partners)) {
          this.buildOnlinetests(result.data.partners);
        }
      }
    }).add(() => {
      this.loading = false;
    });
    this.optionsSalutations = FormHelper.getSelectList(SALUTATIONS, null, this.transloco);
    this.optionsLanguages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
    this.optionsReporttypes = [];
    for (const mode in ReportModes) {
      if (ReportModes.hasOwnProperty(mode)) {
        this.optionsReporttypes.push({
          value: ReportModes[mode],
          label: this.transloco.translate('toolbox_candidate_basedata.reportmodes.' + ReportModes[mode])
        });
      }
    }
    this.form.get('report.type').valueChanges.subscribe(() => {
      this.setReportValidators();
    });
    this.setReportValidators();
    this.ui.setFocus();
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload = {
        businessunit: this.form.get('businessunit').value,
        onlinetest: this.form.get('onlinetest').value,
        language: this.form.get('language').value,
        candidate: this.form.get('candidate').value,
        hiringmanager: this.form.get('hiringmanager').value,
        report: this.form.get('report').value,
      };
      this.api.saveModel('toolbox-candidates', this.order.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.order = new ToolboxCandidateModel().deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  onEdit(): void {
    if (this.auth.hasRight('admin.toolboxcandidate.edit')) {
      this.startEdit();
    }
  }

  startEdit(): void {
    if (this.order !== null) {
      let businessunit = null;
      this.optionsBusinessunits.forEach((actual: SelectItemGroup) => {
        actual.items.forEach((item: SelectItem) => {
          if (item.value === this.order.businessunit?.id) {
            businessunit = item.value;
          }
        });
      });
      let onlinetest = null;
      this.optionsPartners.forEach((actual: SelectItemGroup) => {
        actual.items.forEach((item: SelectItem) => {
          if (item.value === this.order.onlinetest?.id) {
            onlinetest = item.value;
          }
        });
      });
      this.form.patchValue({
        businessunit,
        onlinetest,
        language: this.order.language,
        candidate: {
          gender: this.order.gender,
          firstname: this.order.firstname,
          lastname: this.order.lastname,
          email: this.order.email,
          phone: this.order.phone,
        },
        hiringmanager: {
          gender: this.order.orderer_gender,
          firstname: this.order.orderer_firstname,
          lastname: this.order.orderer_lastname,
          email: this.order.orderer_email,
          phone: this.order.orderer_phone,
          mobile: this.order.orderer_mobile,
        },
        report: {
          type: this.order.report_recipient_type,
          gender: this.order.report_recipient_gender,
          firstname: this.order.report_recipient_firstname,
          lastname: this.order.report_recipient_lastname,
          email: this.order.report_recipient_email
        }
      });
    }
    this.inEdit = true;
    this.setEdit.emit('basedata');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  onDeleteOrder(): void {
    if (this.auth.hasRight('admin.toolboxcandidate.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.order.fullname }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('toolbox-candidates', this.order.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.order.fullname }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/toolbox-candidates']);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  private buildMenu(): void {
    if (this.order !== null) {
      this.items = [];
      if (this.auth.hasRight('admin.toolboxcandidate.delete')) {
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.order.deletable),
          command: () => {
            this.onDeleteOrder();
          }
        });
      }
    }
  }

  private buildBusinessunits(businessunits: Array<any>): void {
    this.optionsBusinessunits = [];
    if (businessunits?.length > 0) {
      businessunits = businessunits.map((businessunit: any) => {
        return new BusinessunitModel().deserialize(businessunit);
      }).sort((a: BusinessunitModel, b: BusinessunitModel) => {
        return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
      });
      businessunits.forEach((businessunit: BusinessunitModel) => {
        const children: SelectItem[] = [];
        if (businessunit.businessunits?.length > 0) {
          businessunit.businessunits.map((child: any) => {
            return new BusinessunitModel().deserialize(child);
          }).sort((a: BusinessunitModel, b: BusinessunitModel) => {
            return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
          }).forEach((childunit: BusinessunitModel) => {
            children.push({
              value: childunit.id,
              label: childunit.name
            });
          });
        }
        if (children.length > 0) {
          this.optionsBusinessunits.push({
            value: businessunit.id,
            label: businessunit.name,
            items: children
          });
        }
      });
    }
  }

  private buildOnlinetests(partners: Array<any>): void {
    this.optionsPartners = [];
    partners = partners.map((partner: any) => {
      return new PartnerModel().deserialize(partner);
    }).sort((a: PartnerModel, b: PartnerModel) => {
      return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
    });
    partners.forEach((partner: PartnerModel) => {
      const onlinetests: SelectItem[] = [];
      partner.onlinetests.map((onlinetest: any) => {
        return new OnlinetestModel().deserialize(onlinetest);
      }).sort((a: OnlinetestModel, b: OnlinetestModel) => {
        return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
      }).forEach((onlinetest: OnlinetestModel) => {
        onlinetests.push({
          value: onlinetest.id,
          label: onlinetest.name
        });
      });
      if (onlinetests.length > 0) {
        this.optionsPartners.push({
          value: partner.id,
          label: partner.name,
          items: onlinetests
        });
      }
    });
  }

  private setReportValidators(): void {
    if (this.hasRecipient) {
      this.form.get('report.firstname').setValidators([Validators.required]);
      this.form.get('report.lastname').setValidators([Validators.required]);
      this.form.get('report.email').setValidators([Validators.required]);
    } else {
      this.form.get('report.firstname').clearValidators();
      this.form.get('report.lastname').clearValidators();
      this.form.get('report.email').clearValidators();
    }
    this.form.get('report.firstname').updateValueAndValidity();
    this.form.get('report.lastname').updateValueAndValidity();
    this.form.get('report.email').updateValueAndValidity();
    if (this.order.status !== 'created') {
      this.form.get('onlinetest').disable();
    }
  }


}
